import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Send, RotateCcw, CheckCircle2, AlertCircle, Eye, Download,
  Copy, Check, Loader2, XCircle, Clock,
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import { useFacility } from '../context/FacilityContext';
import { formatDateTime } from '../utils/datetime';

/**
 * Lease section for the Rental edit page.
 *
 * Renders a state-machine view of the most recent lease for a rental,
 * with operator actions appropriate to each status:
 *
 *   none       -> [Generate lease]
 *   draft      -> View PDF | Send for signature
 *   sent       -> View PDF | Awaiting signature countdown + Resend / Copy URL / Void
 *   signed     -> View signed PDF | Download | signer name + IP
 *   voided     -> [Generate new lease]
 *   expired    -> [Generate new lease]
 *
 * The visible-action map is split into a pure helper (leaseAction) so it
 * can be unit-tested without rendering.
 */

const STATUS_STYLE = {
  draft: 'bg-slate-50 text-slate-600 border border-slate-200/60',
  sent: 'bg-blue-50 text-blue-600 border border-blue-200/60',
  signed: 'bg-emerald-50 text-emerald-600 border border-emerald-200/60',
  voided: 'bg-red-50 text-red-600 border border-red-200/60',
  expired: 'bg-amber-50 text-amber-700 border border-amber-200/60',
};

const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Awaiting signature',
  signed: 'Signed',
  voided: 'Voided',
  expired: 'Expired',
};

// The button matrix per status — exported for the smoke test so the
// JSX-resolved actions can be asserted without rendering.
export function leaseActions(lease) {
  if (!lease) return ['generate'];
  switch (lease.status) {
    case 'draft':
      return ['view_pdf', 'send', 'void'];
    case 'sent':
      return ['view_pdf', 'resend', 'copy_url', 'void'];
    case 'signed':
      return ['view_signed_pdf', 'download_signed'];
    case 'voided':
    case 'expired':
      return ['regenerate'];
    default:
      return [];
  }
}

const buildPublicSignUrl = (token) => {
  if (!token) return '';
  // The public sign page lives at /sign/:token (see App.js).
  if (typeof window === 'undefined') return `/sign/${token}`;
  return `${window.location.origin}/sign/${token}`;
};

const expiresLabel = (s) => {
  if (!s) return null;
  try {
    const diff = new Date(s).getTime() - Date.now();
    if (diff <= 0) return 'expired';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    if (days > 0) return `expires in ${days}d ${hours}h`;
    if (hours > 0) return `expires in ${hours}h`;
    return 'expires soon';
  } catch {
    return null;
  }
};

const RentalLeaseSection = ({ facilityId, rentalId }) => {
  const { currentFacility } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;
  const fmtDate = (s) => formatDateTime(s, timeZone) || '—';
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // action name in flight
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId || !rentalId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/leases/`,
        { params: { rental_id: rentalId } },
      );
      setLeases(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load leases');
    } finally {
      setLoading(false);
    }
  }, [facilityId, rentalId]);

  useEffect(() => { load(); }, [load]);

  // Most recent lease drives the section. Backend already sorts -created_at.
  const lease = leases[0];

  const doAction = async (action, fn) => {
    setBusy(action);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  const generate = () => doAction('generate', async () => {
    await apiClient.post(`/api/facilities/${facilityId}/leases/generate`, {
      rental_id: rentalId,
    });
  });

  const send = () => doAction('send', async () => {
    await apiClient.post(
      `/api/facilities/${facilityId}/leases/${lease._id}/send`,
    );
  });

  const voidLease = () => doAction('void', async () => {
    if (!window.confirm('Void this lease? The signing link will stop working.')) {
      return;
    }
    await apiClient.post(
      `/api/facilities/${facilityId}/leases/${lease._id}/void`,
    );
  });

  const openPdf = async () => {
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/leases/${lease._id}/pdf`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a tick so the new tab has time to read it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load PDF');
    }
  };

  const copyUrl = async () => {
    const url = buildPublicSignUrl(lease?.signing_token);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers / iframes — fall back to a prompt the operator can copy.
      window.prompt('Copy the signing URL:', url);
    }
  };

  if (loading) {
    return (
      <SectionShell>
        <div className="flex items-center gap-2 text-[13px] text-slate-400 py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading lease…
        </div>
      </SectionShell>
    );
  }

  const actions = leaseActions(lease);
  const publicUrl = buildPublicSignUrl(lease?.signing_token);

  return (
    <SectionShell>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" />
            Lease
          </h3>
          <p className="text-[12px] text-slate-400 mt-0.5">
            Storage agreement rendered from the facility's lease template.
          </p>
        </div>
        {lease && (
          <span
            className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${
              STATUS_STYLE[lease.status] || 'bg-slate-50 text-slate-500'
            }`}
          >
            {STATUS_LABEL[lease.status] || lease.status}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-600 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* No lease yet OR re-generate */}
      {(!lease || actions.includes('generate') || actions.includes('regenerate')) && (
        <div className="rounded-md border border-dashed border-slate-200 px-4 py-5 text-center">
          <p className="text-[13px] text-slate-500 mb-3">
            {lease
              ? `Previous lease was ${STATUS_LABEL[lease.status]?.toLowerCase() || lease.status}. Generate a new one to start over.`
              : 'No lease has been generated for this rental yet.'}
          </p>
          <button
            onClick={generate}
            disabled={busy === 'generate'}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === 'generate' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            {lease ? 'Generate new lease' : 'Generate lease'}
          </button>
        </div>
      )}

      {/* Draft */}
      {lease && actions.includes('send') && (
        <div className="space-y-3">
          <p className="text-[12px] text-slate-500">
            Lease draft is ready. Review the PDF, then send it for the
            tenant's signature.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <ActionButton onClick={openPdf} icon={Eye}>View PDF</ActionButton>
            <ActionButton
              onClick={send}
              icon={Send}
              variant="primary"
              loading={busy === 'send'}
            >
              Send for signature
            </ActionButton>
            <ActionButton
              onClick={voidLease}
              icon={XCircle}
              variant="danger"
              loading={busy === 'void'}
            >
              Void
            </ActionButton>
          </div>
        </div>
      )}

      {/* Sent — awaiting signature */}
      {lease && actions.includes('resend') && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            Sent {fmtDate(lease.updated_at)}
            {lease.signing_token_expires_at && (
              <span className="text-slate-400">
                ({expiresLabel(lease.signing_token_expires_at)})
              </span>
            )}
          </div>
          {publicUrl && (
            <div className="rounded-md bg-slate-50 border border-slate-200/80 px-3 py-2 flex items-center gap-2">
              <code className="flex-1 text-[12px] text-slate-700 truncate">
                {publicUrl}
              </code>
              <button
                onClick={copyUrl}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] text-blue-600 hover:bg-blue-50"
                title="Copy signing URL"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ActionButton onClick={openPdf} icon={Eye}>View PDF</ActionButton>
            <ActionButton
              onClick={send}
              icon={RotateCcw}
              loading={busy === 'send'}
            >
              Resend signature email
            </ActionButton>
            <ActionButton
              onClick={voidLease}
              icon={XCircle}
              variant="danger"
              loading={busy === 'void'}
            >
              Void
            </ActionButton>
          </div>
        </div>
      )}

      {/* Signed */}
      {lease && actions.includes('view_signed_pdf') && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[12px] text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Signed on {fmtDate(lease.signed_at)}
          </div>
          {(lease.signature_name || lease.signature_ip) && (
            <div className="rounded-md bg-emerald-50/50 border border-emerald-200/60 px-3 py-2 text-[12px] text-slate-700">
              <div>
                <span className="text-slate-400">Signer:</span>{' '}
                <span className="font-medium">{lease.signature_name || '—'}</span>
              </div>
              {lease.signature_ip && (
                <div>
                  <span className="text-slate-400">IP:</span>{' '}
                  <code className="text-slate-600">{lease.signature_ip}</code>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ActionButton onClick={openPdf} icon={Eye}>View signed PDF</ActionButton>
            <ActionButton
              onClick={() => {
                // Open the same internal endpoint, but with download intent.
                openPdf();
              }}
              icon={Download}
            >
              Download
            </ActionButton>
          </div>
        </div>
      )}
    </SectionShell>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────

const SectionShell = ({ children }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white p-5">
    {children}
  </div>
);

const ActionButton = ({ onClick, icon: Icon, children, variant, loading }) => {
  const styles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    danger: 'border border-red-200 bg-white text-red-600 hover:bg-red-50',
    default: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
  };
  const cls = styles[variant] || styles.default;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors disabled:opacity-50 ${cls}`}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
};

// Test surface — exported for __tests__/RentalLeaseSection.test.js.
export { buildPublicSignUrl };
export default RentalLeaseSection;
